/**
 * AppBackground — fond ambiant, version fluide (perf-first).
 *
 * Refonte 2026-05-23 suite à régressions de fluidité au scroll :
 *  - Plus de particules animées (22 motion.span avec boxShadow = trop coûteux)
 *  - Plus de filter: blur(40px) sur un calque animé
 *  - Plus d'animations en boucle infinie qui forcent un repaint full-viewport
 *  - On garde la signature visuelle : cream + aurora radial + hex grid + dégradé top
 *
 * Tout est statique (transform: none, opacity fixe) — le navigateur peut composer
 * une seule fois et ne touche plus au calque pendant le scroll.
 *
 * Une animation de drift très lente est gardée optionnellement via CSS @keyframes
 * (transform-only, GPU-friendly) — désactivée si prefers-reduced-motion.
 */
import { useReducedMotion } from 'framer-motion';

export function AppBackground() {
  const reduced = useReducedMotion();

  return (
    <div
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden bg-cream"
      aria-hidden
    >
      {/* DNA hairline */}
      <div
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, #3b82f6 25%, #a855f7 50%, #ec4899 75%, transparent 100%)',
          opacity: 0.85,
        }}
      />

      {/* Aurora — gradient radial statique, drift CSS-only optionnel */}
      <div
        className={reduced ? 'absolute inset-0' : 'absolute inset-0 nia-aurora-drift'}
        style={{
          background: `
            radial-gradient(ellipse 65% 55% at 8% 10%, rgba(59,130,246,0.22), transparent 62%),
            radial-gradient(ellipse 60% 50% at 94% 88%, rgba(236,72,153,0.20), transparent 60%),
            radial-gradient(ellipse 55% 50% at 70% 6%, rgba(168,85,247,0.18), transparent 58%),
            radial-gradient(ellipse 50% 45% at 20% 60%, rgba(168,85,247,0.08), transparent 58%)
          `,
          willChange: reduced ? 'auto' : 'transform',
        }}
      />

      {/* Warm top glow */}
      <div
        className="absolute inset-x-0 top-0 h-[55vh]"
        style={{ background: 'radial-gradient(ellipse at 50% -8%, #FFFAF0, transparent 70%)' }}
      />

      {/* Hex grid texture — static, baked into composite layer */}
      <div
        className="absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'56\' height=\'48\' viewBox=\'0 0 56 48\'><path fill=\'none\' stroke=\'%230a1226\' stroke-width=\'1\' d=\'M28 1L52 14.5L52 33.5L28 47L4 33.5L4 14.5Z\'/></svg>")',
          backgroundSize: '56px 48px',
          mixBlendMode: 'multiply',
        }}
      />
    </div>
  );
}
